import * as React from "react";
import styled from "styled-components/native";
import { Modal as DefaultModal, ScrollView } from "react-native";

import {
  background,
  bordered,
  Divider,
  rounded,
  Space,
  spacing,
  Title,
} from "../../styled-component";
import { Theme } from "../../styles";

import Button, { ButtonProps } from "./PressableButton";
import Layout from "./Layout";
import commonStyles from "../../styles/common";
import IconButton from "../../common/components/IconButton";

const ModalBackdrop = styled.Pressable.attrs({
  background: Theme.colors.backdrop,
})`
  ${background};

  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
`;

const ModalContainer = styled.Pressable.attrs({
  background: Theme.colors.background,
})`
  ${background};
  ${rounded};
  ${bordered};

  min-width: 400px;
  max-width: 600px;
  max-height: 100%;
`;

const ModalContentWrapper = styled(Layout.Box).attrs({ size: 1 })``;

const ModalHeaderWrapper = styled(Layout.Row).attrs({
  alignItems: "center",
  justifyContent: "space-between",
})``;

export interface ModalDismissButtonProps extends ButtonProps {}

function ModalDismissButton({ ...buttonProps }: ModalDismissButtonProps) {
  return (
    <Button title="dismiss" color="accent" mode="contained" {...buttonProps} />
  );
}

function ModalDismissIconButton({ ...buttonProps }: ModalDismissButtonProps) {
  return <IconButton name="times" color="accent" {...buttonProps} />;
}

export interface ModalProps {
  isVisible?: boolean;
  onDismiss?: () => void;
  title?: string;
  content?: React.ReactNode;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  isDismissable?: boolean;
  layoutActions?: (props: React.PropsWithChildren<{}>) => JSX.Element;
}

function Modal({
  isVisible,
  onDismiss,
  title,
  content,
  primaryAction,
  secondaryAction,
  isDismissable = true,
  layoutActions: LayoutActions = Layout.Column,
}: ModalProps) {
  return (
    <DefaultModal visible={isVisible} transparent onDismiss={onDismiss}>
      <ModalBackdrop onPress={onDismiss} disabled={!isDismissable}>
        <ScrollView>
          <Space />

          <ModalContainer>
            <ModalContentWrapper>
              {title && (
                <>
                  <ModalHeaderWrapper>
                    <Title numberOfLines={1}>{title}</Title>

                    {onDismiss && (
                      <ModalDismissIconButton
                        onPress={onDismiss}
                        disabled={!isDismissable}
                      />
                    )}
                  </ModalHeaderWrapper>
                  <Divider />
                </>
              )}

              {!!content && !!title && <Space />}

              {content}

              {(!!primaryAction || !!secondaryAction) && (
                <>
                  <Space size={2} />

                  <LayoutActions>
                    {primaryAction}

                    {!!primaryAction && !!secondaryAction && <Space />}

                    {secondaryAction}
                  </LayoutActions>
                </>
              )}
            </ModalContentWrapper>
          </ModalContainer>

          <Space />
        </ScrollView>
      </ModalBackdrop>
    </DefaultModal>
  );
}

Modal.Content = styled.View``;
Modal.DismissButton = ModalDismissButton;
Modal.DismissIconButton = ModalDismissIconButton;

export default Modal;
